import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, AlertTriangle, Zap, RotateCw, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const FullyAutomaticCleaningPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Fully Automatic Cleaning (RAB & AVC) | Inline Filters | CTC Waterworks"
        description="Fully automatic cleaning with RAB (Rotary Auto Brush) and AVC (Automated Vacuum Cycle) for BIG, BIG-S, and GNG inline filters. Continuous operation with minimal downtime and labor."
        canonicalPath="/filtration/inline/cleaning/automatic"
      />
      <Navbar />

      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">Inline Filters</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline/cleaning" className="text-gray-600 hover:text-ctc-blue">Cleaning Options</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Fully Automatic</li>
          </ol>
        </div>
      </nav>

      <section className="pt-16 pb-12 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Fully Automatic Self-Cleaning
            </h1>
            <p className="text-2xl text-gray-600 mb-2 font-light">RAB & AVC</p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Automated ΔP/time-based cleaning cycles for continuous operation with minimal labor and near-zero downtime.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                Fully automatic cleaning systems—RAB (Rotary Auto Brush) and AVC (Automated Vacuum Cycle)—trigger cleaning cycles based on differential pressure sensors or timers. The system runs continuously, cleaning itself without operator intervention, ensuring steady flow rates and optimal filtration performance for 24/7 operations.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Two Fully Automatic Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-8">
              <RotateCw className="text-green-600 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">RAB (Rotary Auto Brush)</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Motorized brush mechanism that automatically rotates against the screen's interior surface on a programmed schedule or when ΔP threshold is reached, continuously dislodging debris without interrupting flow.
              </p>

              <h4 className="font-bold text-gray-900 mb-3">Best For:</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Fine mesh screens with continuous organic fouling</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">24/7 drip irrigation or process water systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Remote sites requiring minimal operator presence</span>
                </li>
              </ul>

              <Link
                to="/filtration/inline/big/brush-cleaning"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold"
              >
                Learn More About Brush Technology
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-8">
              <Droplets className="text-ctc-blue mb-4" size={48} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AVC (Automated Vacuum Cycle)</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Automated vacuum scanner with motorized actuators that initiate suction cycles based on ΔP or timer triggers, pulling debris off the screen and flushing it through a purge valve without manual intervention.
              </p>

              <h4 className="font-bold text-gray-900 mb-3">Best For:</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Coarse screens with heavy debris loads (leaves, algae)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Variable solids loading from surface water sources</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">High-flow systems requiring rapid, frequent purge cycles</span>
                </li>
              </ul>

              <Link
                to="/filtration/inline/big/vacuum-cleaning"
                className="inline-flex items-center text-ctc-blue hover:text-blue-700 font-semibold"
              >
                Learn More About Vacuum Technology
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Fully Automatic: Advantages & Limitations</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                <CheckCircle className="mr-3" size={28} />
                Advantages
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Near-zero downtime—cleaning occurs online without stopping flow</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Minimal labor—no operator intervention required after setup</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Optimized cleaning frequency via ΔP monitoring</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Consistent performance in 24/7 or remote operations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Reduced gasket wear—no repeated disassembly</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                <XCircle className="mr-3" size={28} />
                Limitations
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Highest upfront cost—automation hardware and controls</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Requires electrical power and control system integration</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Periodic maintenance of motors, sensors, and actuators</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Not justified for low duty cycle or seasonal operations</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">May require backup power for critical applications</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Fully Automatic Cleaning Is Best For:</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">High Solids Loading:</span> Surface water sources with variable or heavy debris accumulation.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">24/7 Operations:</span> Continuous process water, drip irrigation, or municipal supply systems.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Remote Sites:</span> Pump stations or field locations with limited operator access.
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <span className="font-semibold text-gray-900">Critical Uptime:</span> Applications where flow interruption is costly or disruptive.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Fully Automatic Compatible Series</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/inline/big"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-ctc-blue"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Supports RAB and AVC fully automatic cleaning assemblies.
              </p>
              <div className="text-ctc-blue font-semibold flex items-center">
                View BIG Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/big-s"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-gray-600"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG-S Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Supports RAB and AVC fully automatic cleaning assemblies.
              </p>
              <div className="text-gray-600 font-semibold flex items-center">
                View BIG-S Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/gng"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-green-600"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">GNG Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Supports RAB and AVC fully automatic cleaning assemblies.
              </p>
              <div className="text-green-600 font-semibold flex items-center">
                View GNG Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>
          </div>

          <div className="mt-8 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertTriangle className="text-orange-600 mr-3 mt-1 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">TIG Series Note</h4>
                <p className="text-gray-700 leading-relaxed">
                  TIG filters do not support Brush/Vacuum/RAB cleaning options. Follow standard TIG maintenance procedures only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Zap className="text-green-600 mx-auto mb-4" size={56} />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Maximize Uptime with Fully Automatic Cleaning</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our filtration specialists can help you design an automated cleaning system tailored to your water quality, flow requirements, and operational goals.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <span>Talk to a Filtration Specialist</span>
              <ArrowRight className="ml-2" size={20} />
            </button>

            <Link
              to="/filtration/inline/cleaning"
              className="border-2 border-green-600 text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-colors inline-flex items-center justify-center"
            >
              Compare All Options
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </div>
  );
};

export default FullyAutomaticCleaningPage;
